var _class, _descriptor, _descriptor2;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { getOwner } from '@ember/application';
import { deprecate } from '@ember/debug';
import { cancel, next } from '@ember/runloop';
import Service from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { convertToFormatjsFormats, createIntl, createIntlCache, formatDate, formatDateRange, formatList, formatMessage, formatNumber, formatRelative, formatRelativeTime, formatTime } from '../-private/formatjs/index';
import { escapeFormatMessageOptions } from '../-private/utils/escape-format-message-options';
import { getDOM } from '../-private/utils/get-dom';
import { convertToArray, convertToString, hasLocaleChanged, normalizeLocale } from '../-private/utils/locale';
import { flattenKeys } from '../-private/utils/translations';
import translations from '../translations';
let IntlService = (_class = class IntlService extends Service {
  get locales() {
    return Object.keys(this._intls);
  }
  get primaryLocale() {
    if (!this._locale) {
      return;
    }
    return this._locale[0];
  }
  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    _initializerDefineProperty(this, "_intls", _descriptor, this);
    _initializerDefineProperty(this, "_locale", _descriptor2, this);
    _defineProperty(this, "_cache", createIntlCache());
    _defineProperty(this, "_formats", {});
    _defineProperty(this, "_onFormatjsError", error => {
      switch (error.code) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        case 'MISSING_DATA':
          {
            console.warn(error.message);
            break;
          }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        case 'MISSING_TRANSLATION':
          {
            // Do nothing
            break;
          }
        default:
          {
            throw error;
          }
      }
    });
    _defineProperty(this, "_onMissingTranslation", (key, locales) => {
      const locale = locales.join(', ');
      return `Missing translation "${key}" for locale "${locale}"`;
    });
    _defineProperty(this, "_timer", void 0);
    const hasNewConfiguration = Boolean(
    // @ts-expect-error: Property 'resolveRegistration' does not exist on type 'Owner'
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    getOwner(this).resolveRegistration('ember-intl:main'));
    if (!hasNewConfiguration) {
      this.getDefaultFormats();
    }

    // Hydrate
    translations.forEach(([locale, translations]) => {
      this.addTranslations(locale, translations);
    });
  }
  addTranslations(locale, translations) {
    const messages = flattenKeys(translations);
    this.updateIntl(locale, messages);
  }
  createIntl(locale, messages = {}) {
    const resolvedLocale = convertToString(locale);
    const formats = this._formats;
    return createIntl({
      defaultFormats: formats,
      defaultLocale: resolvedLocale,
      formats,
      locale: resolvedLocale,
      // @ts-expect-error: Type 'Record<string, unknown>' is not assignable
      messages,
      onError: this._onFormatjsError
    }, this._cache);
  }
  exists(key, locale) {
    const locales = locale ? convertToArray(locale) : this._locale;
    return locales.some(locale => {
      return this.getTranslation(key, locale) !== undefined;
    });
  }
  formatDate(value, options) {
    if (value === undefined || value === null) {
      return '';
    }
    const intlShape = this.getIntlShape(options?.locale);
    return formatDate(intlShape, value, options);
  }
  formatDateRange(from, to, options) {
    if (from === undefined || from === null) {
      return '';
    }
    if (to === undefined || to === null) {
      return '';
    }
    const intlShape = this.getIntlShape(options?.locale);
    return formatDateRange(intlShape, from, to, options);
  }
  formatList(value, options) {
    if (value === undefined || value === null) {
      return '';
    }
    const intlShape = this.getIntlShape(options?.locale);
    return formatList(intlShape, value, options);
  }
  formatMessage(value, options) {
    if (value === undefined || value === null) {
      return '';
    }
    const intlShape = this.getIntlShape(options?.locale);
    const descriptor = typeof value === 'object' ? value : {
      defaultMessage: value,
      description: undefined,
      id: value
    };
    if (options?.htmlSafe) {
      const output = formatMessage(intlShape, descriptor, escapeFormatMessageOptions(options));
      return htmlSafe(output);
    }
    return formatMessage(intlShape, descriptor, options);
  }
  formatNumber(value, options) {
    if (value === undefined || value === null) {
      return '';
    }
    const intlShape = this.getIntlShape(options?.locale);
    return formatNumber(intlShape, value, options);
  }

  /**
   * @deprecated
   *
   * `formatRelative()` will be renamed to `formatRelativeTime()` in `ember-intl@8.0.0`.
   * Please rename the method to `formatRelativeTime()` in your class now.
   */
  formatRelative(value, options) {
    deprecate('formatRelative() will be renamed to formatRelativeTime() in ember-intl@8.0.0. Please rename the method to formatRelativeTime() in your class now.', false, {
      for: 'ember-intl',
      id: 'ember-intl.rename-format-relative-method',
      since: {
        available: '7.2.0',
        enabled: '7.2.0'
      },
      until: '8.0.0',
      url: 'https://ember-intl.github.io/ember-intl/docs/helpers/introduction'
    });
    if (value === undefined || value === null) {
      return '';
    }
    const intlShape = this.getIntlShape(options?.locale);
    return formatRelative(intlShape, value, options?.unit, options);
  }
  formatRelativeTime(value, options) {
    if (value === undefined || value === null) {
      return '';
    }
    const intlShape = this.getIntlShape(options?.locale);
    return formatRelativeTime(intlShape, value, options?.unit, options);
  }
  formatTime(value, options) {
    if (value === undefined || value === null) {
      return '';
    }
    const intlShape = this.getIntlShape(options?.locale);
    return formatTime(intlShape, value, options);
  }

  /**
   * @deprecated
   *
   * Formats, defined in the file `app/formats.js`, will have no effect in
   * `ember-intl@8.0.0`. Please define formats in `app/ember-intl.{js,ts}`,
   * then call `setFormats()` before loading your translations.
   */
  getDefaultFormats() {
    // @ts-expect-error: Property 'resolveRegistration' does not exist on type 'Owner'
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const formats = getOwner(this).resolveRegistration('formats:main');
    this._formats = formats ?? {};
  }
  getIntl(locale) {
    const resolvedLocale = normalizeLocale(convertToString(locale));
    return this._intls[resolvedLocale];
  }
  getIntlShape(locale) {
    if (locale) {
      return this.createIntl(locale);
    }
    return this.getIntl(this._locale);
  }
  getTranslation(key, locale) {
    const messages = this.getIntl(locale)?.messages;
    if (!messages) {
      return;
    }
    return messages[key];
  }
  setFormats(formats) {
    this._formats = convertToFormatjsFormats(formats);

    // Call `updateIntl` to update `formats` for each locale
    this.locales.forEach(locale => {
      this.updateIntl(locale, {});
    });
  }
  setLocale(locale) {
    const proposedLocale = convertToArray(locale);
    if (hasLocaleChanged(proposedLocale, this._locale)) {
      this._locale = proposedLocale;

      // eslint-disable-next-line ember/no-runloop
      cancel(this._timer);

      // eslint-disable-next-line ember/no-runloop
      this._timer = next(() => {
        this.updateDocumentLanguage();
      });
    }
    this.updateIntl(proposedLocale);
  }
  setOnFormatjsError(onFormatjsError) {
    this._onFormatjsError = onFormatjsError;

    // Call `updateIntl` to update `onError` for each locale
    this.locales.forEach(locale => {
      this.updateIntl(locale, {});
    });
  }
  setOnMissingTranslation(onMissingTranslation) {
    this._onMissingTranslation = onMissingTranslation;
  }
  t(key, options) {
    const locales = options?.locale ? [options.locale] : this._locale;
    let translation;
    for (const locale of locales) {
      translation = this.getTranslation(key, locale);
      if (translation !== undefined) {
        break;
      }
    }
    if (translation === undefined) {
      return this._onMissingTranslation(key, locales, options);
    }

    // Bypass @formatjs/intl
    if (translation === '') {
      return '';
    }
    return this.formatMessage({
      defaultMessage: translation,
      id: key
    }, options);
  }
  updateDocumentLanguage() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const dom = getDOM(this);
    if (dom) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const html = dom.documentElement;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      html.setAttribute('lang', this.primaryLocale);
    }
  }
  updateIntl(locale, messages) {
    const resolvedLocale = normalizeLocale(convertToString(locale));
    const intl = this._intls[resolvedLocale];
    let newIntl;
    if (!intl) {
      newIntl = this.createIntl(resolvedLocale, messages);
    } else if (messages) {
      newIntl = this.createIntl(resolvedLocale, {
        ...(intl.messages ?? {}),
        ...messages
      });
    }
    if (!newIntl) {
      return;
    }
    this._intls = {
      ...this._intls,
      [resolvedLocale]: newIntl
    };
  }
  willDestroy() {
    super.willDestroy();

    // eslint-disable-next-line ember/no-runloop
    cancel(this._timer);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_intls", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_locale", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { IntlService as default }; // DO NOT DELETE: this is how TypeScript knows how to look up your services.